import { useEffect, useRef, useState } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { McpAide } from './mcp-aide.tsx'

type Role =
  'proprietaire' | 'admin' | 'gestionnaire' | 'comptabilite' | 'lecture'
type Membre = {
  id: string
  nom: string
  courriel: string
  actif: boolean
  roles: Role[]
  groupes: string[]
  roles_effectifs: Role[]
}
type Groupe = { id: string; nom: string; roles: Role[] }
type Cle = {
  id: string
  nom: string
  membre_nom: string
  prefixe: string
  permissions: string[]
  expire_le_utc: string
  revoquee_le_utc: string | null
  derniere_utilisation_utc: string | null
}
type ConnexionOAuth = {
  id: string
  client_id: string
  client_name: string
  membre_nom: string
  scopes: string[]
  cree_le_utc: string
  expire_le_utc: string
}
type Donnees = {
  moi: { membre_id: string; roles: Role[] }
  membres: Membre[]
  groupes: Groupe[]
  cles: Cle[]
  connexions_oauth: ConnexionOAuth[]
  autorisations_api: string[]
  mcp_url: string
  api_url: string
}
const roles: Record<Role, { nom: string; description: string }> = {
  proprietaire: {
    nom: 'Propriétaire',
    description: 'Tous les accès, y compris la gestion des propriétaires.',
  },
  admin: {
    nom: 'Administrateur',
    description: 'Gère les événements, les finances et l’équipe.',
  },
  gestionnaire: {
    nom: 'Gestionnaire',
    description: 'Crée les événements et traite les réservations.',
  },
  comptabilite: {
    nom: 'Comptabilité',
    description: 'Consulte les réservations et gère les finances.',
  },
  lecture: {
    nom: 'Consultation',
    description: 'Consulte les événements et les réservations.',
  },
}
const scopes: Record<string, { nom: string; description: string }> = {
  'services.lire': {
    nom: 'Événements',
    description: 'Consulter les événements et leur état.',
  },
  'demandes.lire': {
    nom: 'Réservations',
    description: 'Lire les réservations et les coordonnées des demandeurs.',
  },
  'finances.lire': {
    nom: 'Rapports financiers',
    description: 'Consulter les montants et les transactions.',
  },
  'finances.exporter': {
    nom: 'Exports financiers',
    description: 'Accéder aux exports comptables autorisés.',
  },
}
const date = (v: string) =>
  new Intl.DateTimeFormat('fr-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(v))

function Dialogue({
  titre,
  enfants,
  fermer,
  occupe,
}: {
  titre: string
  enfants: ComponentChildren
  fermer(): void
  occupe: boolean
}) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const avant = document.activeElement as HTMLElement | null
    const dialogue = ref.current
    dialogue?.showModal()
    return () => {
      dialogue?.close()
      avant?.focus()
    }
  }, [])
  return (
    <dialog
      ref={ref}
      class="ac-dialog"
      aria-label={titre}
      onCancel={(e) => {
        e.preventDefault()
        if (!occupe) fermer()
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !occupe) fermer()
      }}
    >
      <div class="ac-dialog-head">
        <h2>{titre}</h2>
        <button
          type="button"
          class="ac-icon"
          aria-label="Fermer"
          disabled={occupe}
          onClick={fermer}
        >
          ×
        </button>
      </div>
      {enfants}
    </dialog>
  )
}

export function AccesPane({
  baseUrl = '',
  permissions,
  onAuth,
  sectionInitiale = 'equipe',
}: {
  baseUrl?: string
  permissions: string[]
  onAuth?: () => void
  sectionInitiale?: 'equipe' | 'integrations'
}) {
  const [donnees, setDonnees] = useState<Donnees | null>(null)
  const [onglet, setOnglet] = useState<'membres' | 'groupes' | 'connexions'>(
    sectionInitiale === 'integrations' ? 'connexions' : 'membres',
  )
  const [recherche, setRecherche] = useState('')
  const [erreur, setErreur] = useState('')
  const [message, setMessage] = useState('')
  const [occupe, setOccupe] = useState(false)
  const enCours = useRef(false)
  const retourFocus = useRef<HTMLElement | null>(null)
  const [dialogue, setDialogue] = useState<
    | 'membre'
    | 'groupe'
    | 'cle'
    | 'secret'
    | 'statut'
    | 'revoquer'
    | 'revoquer-oauth'
    | 'inviter'
    | 'supprimer-groupe'
    | null
  >(null)
  const [cible, setCible] = useState<Membre | null>(null)
  const [groupe, setGroupe] = useState<Groupe | null>(null)
  const [cle, setCle] = useState<Cle | null>(null)
  const [connexionOAuth, setConnexionOAuth] = useState<ConnexionOAuth | null>(
    null,
  )
  const [nom, setNom] = useState('')
  const [courriel, setCourriel] = useState('')
  const [role, setRole] = useState<Role>('lecture')
  const [groupesChoisis, setGroupesChoisis] = useState<string[]>([])
  const [portees, setPortees] = useState<string[]>(['services.lire'])
  const [jours, setJours] = useState('90')
  const [secret, setSecret] = useState('')
  const [visible, setVisible] = useState(false)
  const [erreurDialogue, setErreurDialogue] = useState('')
  const equipePermise = permissions.includes('acces.gerer'),
    integrationsPermises = permissions.includes('integrations.gerer')
  const proprietaire = donnees?.moi.roles.includes('proprietaire') ?? false
  const base = baseUrl.replace(/\/$/, '')
  const demander = async (route: string, options?: RequestInit) => {
    const r = await fetch(`${base}/api/admin/acces${route}`, {
      ...options,
      headers: { 'content-type': 'application/json', ...options?.headers },
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) {
      if (r.status === 401) onAuth?.()
      throw new Error(
        data.erreur?.message ??
          'Impossible de joindre le serveur. Réessayez dans un instant.',
      )
    }
    return data
  }
  const charger = async (signal?: AbortSignal) => {
    const data = await demander('', { signal })
    setDonnees(data)
    setErreur('')
  }
  useEffect(() => {
    const ctrl = new AbortController()
    void charger(ctrl.signal).catch((e) => {
      if (!ctrl.signal.aborted) setErreur(e.message)
    })
    return () => ctrl.abort()
  }, [baseUrl])
  useEffect(() => {
    setOnglet(sectionInitiale === 'integrations' ? 'connexions' : 'membres')
  }, [sectionInitiale])
  const restaurerFocus = () =>
    requestAnimationFrame(() => retourFocus.current?.focus())
  const fermer = () => {
    if (occupe) return
    setDialogue(null)
    setSecret('')
    setVisible(false)
    setErreurDialogue('')
    restaurerFocus()
  }
  const ouvrir = (type: NonNullable<typeof dialogue>) => {
    retourFocus.current = document.activeElement as HTMLElement | null
    setErreurDialogue('')
    setMessage('')
    setDialogue(type)
  }
  const executer = async (
    route: string,
    method: string,
    body: unknown,
    succes: string,
  ) => {
    if (enCours.current) return
    enCours.current = true
    setOccupe(true)
    setErreurDialogue('')
    try {
      const resultat = await demander(route, {
        method,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      })
      if (resultat.secret) {
        setSecret(resultat.secret)
        setVisible(false)
        setDialogue('secret')
      } else {
        setDialogue(null)
        setMessage(resultat.message ?? succes)
        restaurerFocus()
      }
      await charger().catch(() =>
        setErreur(
          'La modification est enregistrée. Actualisez la liste pour voir le résultat.',
        ),
      )
    } catch (e) {
      setErreurDialogue(
        e instanceof Error
          ? e.message
          : 'Cette modification n’a pas pu être enregistrée.',
      )
    } finally {
      enCours.current = false
      setOccupe(false)
    }
  }
  const copier = async (
    texte: string,
    succes = 'Copié dans le presse-papiers.',
  ) => {
    try {
      await navigator.clipboard.writeText(texte)
      setMessage(succes)
    } catch {
      setMessage(
        'La copie automatique est indisponible. Sélectionnez le texte pour le copier.',
      )
    }
  }
  const membreForm = (membre: Membre | null) => {
    setCible(membre)
    setNom(membre?.nom ?? '')
    setCourriel(membre?.courriel ?? '')
    setRole(membre?.roles[0] ?? 'lecture')
    setGroupesChoisis(membre?.groupes ?? [])
    ouvrir('membre')
  }
  const groupeForm = (g: Groupe | null) => {
    setGroupe(g)
    setNom(g?.nom ?? '')
    setRole(g?.roles[0] ?? 'lecture')
    ouvrir('groupe')
  }
  const choixRole = (
    <fieldset class="ac-role-choices">
      <legend>Niveau d’accès</legend>
      {(Object.keys(roles) as Role[])
        .filter((r) => r !== 'proprietaire' || proprietaire)
        .map((r) => (
          <label class={role === r ? 'selectionne' : ''} key={r}>
            <input
              type="radio"
              name="role"
              value={r}
              checked={role === r}
              onChange={() => setRole(r)}
            />
            <span>
              <strong>{roles[r].nom}</strong>
              <small>{roles[r].description}</small>
            </span>
          </label>
        ))}
    </fieldset>
  )
  const piedForm = (libelle: string) => (
    <>
      <p class="ac-form-error" role="alert">
        {erreurDialogue}
      </p>
      <div class="ac-dialog-actions">
        <button
          type="button"
          class="ac-button"
          disabled={occupe}
          onClick={fermer}
        >
          Annuler
        </button>
        <button type="submit" class="ac-button primary" disabled={occupe}>
          {occupe ? 'Enregistrement…' : libelle}
        </button>
      </div>
    </>
  )
  const actionsConfirmation = (
    action: () => void,
    libelle: string,
    danger = false,
  ) => (
    <>
      <p class="ac-form-error" role="alert">
        {erreurDialogue}
      </p>
      <div class="ac-dialog-actions">
        <button class="ac-button" disabled={occupe} onClick={fermer}>
          Annuler
        </button>
        <button
          class={`ac-button ${danger ? 'danger' : 'primary'}`}
          disabled={occupe}
          onClick={action}
        >
          {occupe ? 'Un instant…' : libelle}
        </button>
      </div>
    </>
  )
  const filtrer = (m: Membre) =>
    `${m.nom} ${m.courriel}`
      .toLocaleLowerCase()
      .includes(recherche.toLocaleLowerCase())

  if (!equipePermise && !integrationsPermises)
    return (
      <section class="acces-pane">
        <div class="ac-empty">
          <h2>Votre accès</h2>
          <p>
            Pour modifier l’équipe ou les connexions, contactez un
            administrateur.
          </p>
        </div>
      </section>
    )
  return (
    <section class="acces-pane">
      <header class="ac-page-head">
        <div>
          <h2>
            {onglet === 'connexions'
              ? 'Vos outils connectés'
              : 'Travaillez ensemble'}
          </h2>
          <p>
            {onglet === 'connexions'
              ? 'Partagez uniquement les données nécessaires.'
              : 'Choisissez ce que chacun peut voir et modifier.'}
          </p>
        </div>
        {onglet === 'membres' && equipePermise && (
          <button class="ac-button primary" onClick={() => membreForm(null)}>
            + Ajouter un membre
          </button>
        )}
        {onglet === 'groupes' && equipePermise && (
          <button class="ac-button primary" onClick={() => groupeForm(null)}>
            + Créer un groupe
          </button>
        )}
        {onglet === 'connexions' && integrationsPermises && (
          <button
            class="ac-button primary"
            onClick={() => {
              setNom('')
              setPortees(['services.lire'])
              setJours('90')
              ouvrir('cle')
            }}
          >
            + Nouvelle clé API
          </button>
        )}
      </header>
      <div class="ac-tabs" aria-label="Accès et connexions">
        {equipePermise && (
          <>
            <button
              aria-pressed={onglet === 'membres'}
              onClick={() => setOnglet('membres')}
            >
              Membres
              {donnees && (
                <span>{donnees.membres.filter((m) => m.actif).length}</span>
              )}
            </button>
            <button
              aria-pressed={onglet === 'groupes'}
              onClick={() => setOnglet('groupes')}
            >
              Groupes
            </button>
          </>
        )}
        {integrationsPermises && (
          <button
            aria-pressed={onglet === 'connexions'}
            onClick={() => setOnglet('connexions')}
          >
            Connexions
          </button>
        )}
      </div>
      {message && (
        <div class="ac-notice" role="status">
          {message}
          <button
            class="ac-icon"
            aria-label="Fermer le message"
            onClick={() => setMessage('')}
          >
            ×
          </button>
        </div>
      )}
      {erreur && (
        <div class="ac-notice erreur" role="alert">
          {erreur}
          <button
            class="ac-button"
            onClick={() => void charger().catch((e) => setErreur(e.message))}
          >
            Réessayer
          </button>
        </div>
      )}
      {!donnees && !erreur && (
        <p class="ac-loading" role="status">
          Chargement de votre équipe…
        </p>
      )}
      {donnees && onglet === 'membres' && equipePermise && (
        <div class="ac-panel">
          <div class="ac-toolbar">
            <div>
              <h2>Les membres</h2>
              <p>
                {donnees.membres.filter((m) => m.actif).length} accès actifs
                dans votre organisation
              </p>
            </div>
            <label class="ac-search">
              <span class="ac-sr">Rechercher un membre</span>
              <input
                type="search"
                placeholder="Rechercher un membre"
                value={recherche}
                onInput={(e) => setRecherche(e.currentTarget.value)}
              />
            </label>
          </div>
          <div class="ac-member-list">
            {donnees.membres.filter(filtrer).map((m) => (
              <article
                class={`ac-member ${m.actif ? '' : 'inactive'}`}
                key={m.id}
              >
                <div class="ac-avatar" aria-hidden="true">
                  {m.nom
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()}
                </div>
                <div class="ac-member-name">
                  <strong>
                    {m.nom}
                    {m.id === donnees.moi.membre_id && <small>Vous</small>}
                  </strong>
                  <span>{m.courriel}</span>
                  {m.groupes.length > 0 && (
                    <small>
                      {m.groupes
                        .map(
                          (id) => donnees.groupes.find((g) => g.id === id)?.nom,
                        )
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  )}
                </div>
                <div class="ac-member-role">
                  <span class="ac-badge">
                    {m.roles_effectifs.map((r) => roles[r].nom).join(' · ') ||
                      'Aucun rôle'}
                  </span>
                  {!m.actif && <span class="ac-muted">Désactivé</span>}
                </div>
                <div class="ac-row-actions">
                  {(proprietaire ||
                    !m.roles_effectifs.includes('proprietaire')) && (
                    <>
                      <button
                        class="ac-button subtle"
                        onClick={() => membreForm(m)}
                      >
                        Modifier<span class="ac-sr"> {m.nom}</span>
                      </button>
                      <details class="ac-menu">
                        <summary aria-label={`Autres actions pour ${m.nom}`}>
                          •••
                        </summary>
                        <div>
                          {m.actif && (
                            <button
                              onClick={(e) => {
                                e.currentTarget
                                  .closest('details')
                                  ?.removeAttribute('open')
                                setCible(m)
                                ouvrir('inviter')
                              }}
                            >
                              Envoyer un lien d’accès
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.currentTarget
                                .closest('details')
                                ?.removeAttribute('open')
                              setCible(m)
                              ouvrir('statut')
                            }}
                          >
                            {m.actif
                              ? 'Désactiver l’accès'
                              : 'Réactiver l’accès'}
                          </button>
                        </div>
                      </details>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
          {!donnees.membres.some(filtrer) && (
            <div class="ac-empty">
              <h3>Aucun membre trouvé</h3>
              <p>Essayez un autre nom ou une autre adresse courriel.</p>
            </div>
          )}
          <footer class="ac-panel-note">
            Les membres peuvent se connecter avec un lien par courriel. Un mot
            de passe peut aussi être provisionné pour un accès de démonstration.
          </footer>
        </div>
      )}
      {donnees && onglet === 'groupes' && equipePermise && (
        <>
          <p class="ac-section-intro">
            Réunissez les personnes qui travaillent ensemble. Les membres d’un
            groupe héritent de son rôle, en plus de leur accès personnel.
          </p>
          {donnees.groupes.length === 0 ? (
            <div class="ac-empty ac-panel">
              <div class="ac-empty-symbol" aria-hidden="true">
                ◎
              </div>
              <h2>Les bons accès, en une fois.</h2>
              <p>
                Créez un groupe pour l’accueil, la comptabilité ou votre équipe
                d’événements.
              </p>
              <button
                class="ac-button primary"
                onClick={() => groupeForm(null)}
              >
                Créer le premier groupe
              </button>
            </div>
          ) : (
            <div class="ac-group-grid">
              {donnees.groupes.map((g) => (
                <article class="ac-panel ac-group" key={g.id}>
                  <div class="ac-group-mark" aria-hidden="true">
                    ◎
                  </div>
                  <h2>{g.nom}</h2>
                  <p>
                    {
                      donnees.membres.filter((m) => m.groupes.includes(g.id))
                        .length
                    }{' '}
                    membre(s)
                  </p>
                  <span class="ac-badge">
                    {g.roles.map((r) => roles[r].nom).join(' · ')}
                  </span>
                  <div class="ac-group-actions">
                    {(proprietaire || !g.roles.includes('proprietaire')) && (
                      <>
                        <button class="ac-button" onClick={() => groupeForm(g)}>
                          Modifier le groupe
                        </button>
                        <button
                          class="ac-button subtle"
                          onClick={() => {
                            setGroupe(g)
                            ouvrir('supprimer-groupe')
                          }}
                        >
                          Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}
      {donnees && onglet === 'connexions' && integrationsPermises && (
        <>
          <div class="ac-connect-intro ac-panel">
            <div class="ac-connect-symbol" aria-hidden="true">
              ↗
            </div>
            <div>
              <h2>Vos données, dans vos outils.</h2>
              <p>
                Connectez un assistant avec OAuth, sans lui confier de clé. Les
                clés personnelles ci-dessous restent disponibles pour les API
                et les clients avancés qui acceptent un en-tête personnalisé.
              </p>
              <span class="ac-badge">Lecture uniquement</span>
            </div>
          </div>
          <div class="ac-panel">
            <div class="ac-toolbar">
              <div>
                <h2>Connexions OAuth actives</h2>
                <p>
                  Révoquez ici l’accès côté Funo. Se déconnecter dans un client
                  supprime ses identifiants locaux, mais ne garantit pas cette
                  révocation distante.
                </p>
              </div>
            </div>
            {donnees.connexions_oauth.length === 0 ? (
              <div class="ac-empty">
                <h3>Aucun assistant autorisé</h3>
                <p>
                  Les connexions apparaissent ici après un consentement OAuth
                  réussi.
                </p>
              </div>
            ) : (
              donnees.connexions_oauth.map((connexion) => (
                <article class="ac-key" key={connexion.id}>
                  <div>
                    <strong>{connexion.client_name}</strong>
                    <p>
                      <code>{connexion.client_id}</code>
                      <span class="ac-badge">OAuth active</span>
                    </p>
                    <small>
                      {connexion.scopes
                        .map((scope) => scopes[scope]?.nom ?? scope)
                        .join(' · ')}
                    </small>
                    <small>
                      {connexion.membre_nom} · Autorisée le{' '}
                      {date(connexion.cree_le_utc)} · Expire le{' '}
                      {date(connexion.expire_le_utc)}
                    </small>
                  </div>
                  <button
                    class="ac-button"
                    onClick={() => {
                      setConnexionOAuth(connexion)
                      ouvrir('revoquer-oauth')
                    }}
                  >
                    Révoquer
                    <span class="ac-sr"> {connexion.client_name}</span>
                  </button>
                </article>
              ))
            )}
          </div>
          <div class="ac-panel">
            <div class="ac-toolbar">
              <div>
                <h2>Clés API personnelles</h2>
                <p>
                  Facultatives. Les clés complètes ne sont jamais affichées à
                  nouveau.
                </p>
              </div>
            </div>
            {donnees.cles.length === 0 ? (
              <div class="ac-empty">
                <h3>Aucune connexion pour le moment</h3>
                <p>
                  Créez-en une seulement pour une intégration qui ne prend pas
                  OAuth en charge.
                </p>
                <button
                  class="ac-button"
                  onClick={() => {
                    setNom('')
                    setPortees(['services.lire'])
                    setJours('90')
                    ouvrir('cle')
                  }}
                >
                  Créer une clé API
                </button>
              </div>
            ) : (
              donnees.cles.map((c) => {
                const expiree = Date.parse(c.expire_le_utc) <= Date.now()
                return (
                  <article class="ac-key" key={c.id}>
                    <div>
                      <strong>{c.nom}</strong>
                      <p>
                        <code>{c.prefixe}••••••••</code>
                        <span class="ac-badge">
                          {c.revoquee_le_utc
                            ? 'Révoquée'
                            : expiree
                              ? 'Expirée'
                              : 'Active'}
                        </span>
                      </p>
                      <small>
                        {c.permissions
                          .map((s) => scopes[s]?.nom ?? s)
                          .join(' · ')}
                      </small>
                      <small>
                        {c.membre_nom} · Expire le {date(c.expire_le_utc)} ·{' '}
                        {c.derniere_utilisation_utc
                          ? `Utilisée le ${date(c.derniere_utilisation_utc)}`
                          : 'Pas encore utilisée'}
                      </small>
                    </div>
                    {!c.revoquee_le_utc && (
                      <button
                        class="ac-button"
                        onClick={() => {
                          setCle(c)
                          ouvrir('revoquer')
                        }}
                      >
                        Révoquer<span class="ac-sr"> {c.nom}</span>
                      </button>
                    )}
                  </article>
                )
              })
            )}
          </div>
          <McpAide mcpUrl={donnees.mcp_url} />
          <div class="ac-panel ac-instructions">
            <h2>API HTTP avancée</h2>
            <p>
              Les clés personnelles servent uniquement aux intégrations qui
              savent protéger un secret. Envoyez la clé dans l’en-tête HTTP{' '}
              <code>Authorization</code>; ne la placez jamais dans l’adresse.
            </p>
            <p>
              Point d’entrée : <code>{donnees.api_url}/evenements</code>.
            </p>
          </div>
        </>
      )}
      {dialogue === 'membre' && donnees && (
        <Dialogue
          titre={cible ? 'Modifier un accès' : 'Ajouter un membre'}
          fermer={fermer}
          occupe={occupe}
          enfants={
            <form
              class="ac-form"
              onSubmit={(e) => {
                e.preventDefault()
                void executer(
                  `/membres${cible ? `/${cible.id}` : ''}`,
                  cible ? 'PATCH' : 'POST',
                  { nom, courriel, roles: [role], groupes: groupesChoisis },
                  cible
                    ? 'L’accès a été mis à jour.'
                    : 'Membre ajouté. Il peut demander son lien depuis la page de connexion.',
                )
              }}
            >
              <label>
                Nom complet
                <input
                  required
                  autoFocus
                  maxLength={100}
                  value={nom}
                  onInput={(e) => setNom(e.currentTarget.value)}
                  autoComplete="name"
                />
              </label>
              <label>
                Adresse courriel
                <input
                  required
                  type="email"
                  maxLength={254}
                  disabled={Boolean(cible)}
                  value={courriel}
                  onInput={(e) => setCourriel(e.currentTarget.value)}
                  autoComplete="email"
                />
              </label>
              {choixRole}
              {donnees.groupes.length > 0 && (
                <fieldset class="ac-checks">
                  <legend>
                    Groupes <span>Facultatif</span>
                  </legend>
                  {donnees.groupes
                    .filter(
                      (g) => proprietaire || !g.roles.includes('proprietaire'),
                    )
                    .map((g) => (
                      <label key={g.id}>
                        <input
                          type="checkbox"
                          checked={groupesChoisis.includes(g.id)}
                          onChange={(e) =>
                            setGroupesChoisis(
                              e.currentTarget.checked
                                ? [...groupesChoisis, g.id]
                                : groupesChoisis.filter((id) => id !== g.id),
                            )
                          }
                        />
                        <span>
                          {g.nom}
                          <small>
                            {g.roles.map((r) => roles[r].nom).join(' · ')}
                          </small>
                        </span>
                      </label>
                    ))}
                </fieldset>
              )}
              {!cible && (
                <p class="ac-hint">
                  Aucun courriel n’est envoyé à cette étape. Vous pourrez
                  envoyer un lien d’accès depuis la fiche du membre.
                </p>
              )}
              {piedForm(cible ? 'Enregistrer' : 'Ajouter le membre')}
            </form>
          }
        />
      )}
      {dialogue === 'groupe' && (
        <Dialogue
          titre={groupe ? 'Modifier le groupe' : 'Créer un groupe'}
          fermer={fermer}
          occupe={occupe}
          enfants={
            <form
              class="ac-form"
              onSubmit={(e) => {
                e.preventDefault()
                void executer(
                  `/groupes${groupe ? `/${groupe.id}` : ''}`,
                  groupe ? 'PATCH' : 'POST',
                  { nom, roles: [role] },
                  'Le groupe a été enregistré.',
                )
              }}
            >
              <label>
                Nom du groupe
                <input
                  required
                  autoFocus
                  maxLength={100}
                  placeholder="Ex. Équipe de l’accueil"
                  value={nom}
                  onInput={(e) => setNom(e.currentTarget.value)}
                />
              </label>
              {choixRole}
              <p class="ac-hint">
                Tous les membres du groupe héritent de ce rôle. Ajoutez-les
                depuis leur fiche.
              </p>
              {piedForm(groupe ? 'Enregistrer' : 'Créer le groupe')}
            </form>
          }
        />
      )}
      {dialogue === 'cle' && donnees && (
        <Dialogue
          titre="Nouvelle connexion"
          fermer={fermer}
          occupe={occupe}
          enfants={
            <form
              class="ac-form"
              onSubmit={(e) => {
                e.preventDefault()
                void executer(
                  '/cles',
                  'POST',
                  { nom, permissions: portees, jours: Number(jours) },
                  'Connexion créée.',
                )
              }}
            >
              <label>
                Nom de la connexion
                <input
                  required
                  autoFocus
                  maxLength={100}
                  placeholder="Ex. Assistant de l’équipe"
                  value={nom}
                  onInput={(e) => setNom(e.currentTarget.value)}
                />
              </label>
              <fieldset class="ac-checks">
                <legend>Données accessibles</legend>
                {donnees.autorisations_api.map((s) => (
                  <label key={s}>
                    <input
                      type="checkbox"
                      checked={portees.includes(s)}
                      onChange={(e) =>
                        setPortees(
                          e.currentTarget.checked
                            ? [...portees, s]
                            : portees.filter((p) => p !== s),
                        )
                      }
                    />
                    <span>
                      <strong>{scopes[s]?.nom ?? s}</strong>
                      <small>{scopes[s]?.description}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
              <label>
                Expiration
                <select
                  value={jours}
                  onChange={(e) => setJours(e.currentTarget.value)}
                >
                  <option value="30">Dans 30 jours</option>
                  <option value="90">Dans 90 jours</option>
                  <option value="365">Dans un an</option>
                </select>
              </label>
              <p class="ac-hint">
                Cette connexion pourra seulement lire les données sélectionnées.
                Elle ne pourra jamais modifier une réservation.
              </p>
              {piedForm('Créer la clé')}
            </form>
          }
        />
      )}
      {dialogue === 'secret' && (
        <Dialogue
          titre="Votre connexion est prête"
          fermer={fermer}
          occupe={occupe}
          enfants={
            <div class="ac-form">
              <p>
                Copiez cette clé dans votre outil maintenant. Elle ne sera plus
                accessible après la fermeture de cette fenêtre.
              </p>
              <label>
                Clé privée
                <div class="ac-copy-field">
                  <input
                    type={visible ? 'text' : 'password'}
                    readOnly
                    value={secret}
                    autoComplete="off"
                    spellcheck={false}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    class="ac-button"
                    onClick={() =>
                      void copier(
                        secret,
                        'Clé copiée. Collez-la dans votre outil.',
                      )
                    }
                  >
                    Copier
                  </button>
                </div>
              </label>
              <label class="ac-inline-check">
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={(e) => setVisible(e.currentTarget.checked)}
                />
                Afficher la clé
              </label>
              {message && (
                <p class="ac-hint" role="status">
                  {message}
                </p>
              )}
              <div class="ac-dialog-actions">
                <button class="ac-button primary" onClick={fermer}>
                  J’ai conservé ma clé
                </button>
              </div>
            </div>
          }
        />
      )}
      {dialogue === 'statut' && cible && (
        <Dialogue
          titre={
            cible.actif ? 'Désactiver cet accès ?' : 'Réactiver cet accès ?'
          }
          fermer={fermer}
          occupe={occupe}
          enfants={
            <div class="ac-form">
              <p>
                {cible.actif
                  ? `${cible.nom} ne pourra plus se connecter. Ses sessions et ses clés seront révoquées immédiatement.`
                  : `${cible.nom} pourra de nouveau demander un lien de connexion. Ses anciennes clés restent révoquées.`}
              </p>
              {actionsConfirmation(
                () =>
                  void executer(
                    `/membres/${cible.id}`,
                    'PATCH',
                    { actif: !cible.actif },
                    cible.actif ? 'Accès désactivé.' : 'Accès réactivé.',
                  ),
                cible.actif ? 'Désactiver l’accès' : 'Réactiver l’accès',
                cible.actif,
              )}
            </div>
          }
        />
      )}
      {dialogue === 'revoquer' && cle && (
        <Dialogue
          titre="Révoquer cette connexion ?"
          fermer={fermer}
          occupe={occupe}
          enfants={
            <div class="ac-form">
              <p>
                « {cle.nom} » perdra immédiatement son accès. Vous pourrez créer
                une nouvelle clé si nécessaire.
              </p>
              {actionsConfirmation(
                () =>
                  void executer(
                    `/cles/${cle.id}`,
                    'DELETE',
                    undefined,
                    'La connexion a été révoquée.',
                  ),
                'Révoquer la clé',
                true,
              )}
            </div>
          }
        />
      )}
      {dialogue === 'revoquer-oauth' && connexionOAuth && (
        <Dialogue
          titre="Révoquer cette autorisation OAuth ?"
          fermer={fermer}
          occupe={occupe}
          enfants={
            <div class="ac-form">
              <p>
                « {connexionOAuth.client_name} » perdra immédiatement son accès
                côté Funo. Une nouvelle connexion exigera un nouveau
                consentement.
              </p>
              {actionsConfirmation(
                () =>
                  void executer(
                    `/oauth/${connexionOAuth.id}`,
                    'DELETE',
                    undefined,
                    'L’autorisation OAuth a été révoquée.',
                  ),
                'Révoquer l’autorisation',
                true,
              )}
            </div>
          }
        />
      )}
      {dialogue === 'inviter' && cible && (
        <Dialogue
          titre="Envoyer un lien d’accès ?"
          fermer={fermer}
          occupe={occupe}
          enfants={
            <div class="ac-form">
              <p>
                Un courriel contenant un lien valable vingt minutes sera envoyé
                à <strong>{cible.courriel}</strong>.
              </p>
              {actionsConfirmation(
                () =>
                  void executer(
                    `/membres/${cible.id}/invitation`,
                    'POST',
                    {},
                    'Invitation envoyée.',
                  ),
                'Envoyer le lien',
              )}
            </div>
          }
        />
      )}
      {dialogue === 'supprimer-groupe' && groupe && (
        <Dialogue
          titre="Supprimer ce groupe ?"
          fermer={fermer}
          occupe={occupe}
          enfants={
            <div class="ac-form">
              <p>
                Les membres de « {groupe.nom} » conserveront leur accès
                personnel et leurs autres groupes. Ils perdront les
                autorisations héritées de ce groupe.
              </p>
              {actionsConfirmation(
                () =>
                  void executer(
                    `/groupes/${groupe.id}`,
                    'DELETE',
                    undefined,
                    'Le groupe a été supprimé.',
                  ),
                'Supprimer le groupe',
                true,
              )}
            </div>
          }
        />
      )}
    </section>
  )
}

export const stylesAcces = `
.ac-form input[type="radio"],.ac-form input[type="checkbox"]{flex:0 0 16px;width:16px;height:16px;min-height:0;min-width:0;padding:0;border:initial;box-shadow:none;border-radius:initial}.ac-role-choices label>span,.ac-checks label>span{flex:1;min-width:0}.ac-dialog{overflow-x:hidden}.ac-form fieldset{min-width:0}
.acces-pane{color:#2a1632;max-width:1120px;margin:auto;padding:8px 0 40px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;line-height:1.5}.acces-pane *{box-sizing:border-box}.acces-pane button,.acces-pane input,.acces-pane select{font:inherit}.acces-pane button{cursor:pointer}.acces-pane button:disabled{opacity:.5;cursor:wait}.acces-pane h1,.acces-pane h2,.acces-pane h3,.acces-pane p{margin:0}.acces-pane h1{font-size:clamp(28px,3.2vw,38px);line-height:1.14;letter-spacing:-1.2px;font-weight:650}.acces-pane h2{font-size:18px;letter-spacing:-.35px;font-weight:650}.acces-pane h3{font-size:17px}.acces-pane p,.ac-muted{color:#6b5a70}.ac-page-head{display:flex;align-items:center;justify-content:space-between;gap:24px;margin:16px 0 30px}.ac-page-head p:last-child{margin-top:12px;max-width:560px;font-size:15px}.ac-eyebrow{font-size:10px;letter-spacing:1.6px;font-weight:650;margin-bottom:10px!important}.ac-button{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:38px;border:1px solid #e1dae4;border-radius:10px;background:#fff;color:#2a1632;padding:8px 14px;font-size:13px!important;font-weight:550!important;text-decoration:none;white-space:nowrap}.ac-button:hover{background:#f7f3f9}.ac-button.primary{background:#391d44;border-color:#391d44;color:#fff}.ac-button.primary:hover{background:#391d44}.ac-button.danger{background:#c42b25;border-color:#c42b25;color:#fff}.ac-button.subtle{border-color:transparent;background:transparent;color:#391d44}.ac-icon{border:0;background:transparent;font-size:24px!important;color:#6b5a70;line-height:1;padding:6px}.ac-tabs{display:flex;align-items:center;gap:4px;background:#f0e8f3;width:fit-content;padding:4px;border-radius:12px;margin-bottom:24px}.ac-tabs button{border:0;background:transparent;border-radius:9px;padding:8px 18px;font-size:13px;font-weight:550;color:#6b5a70}.ac-tabs button[aria-pressed=true]{background:#fff;box-shadow:0 1px 4px #00000014;color:#2a1632}.ac-tabs span{margin-left:7px;font-size:11px;color:#6b5a70}.ac-panel{background:#fff;border:1px solid #eee4f1;border-radius:18px;margin-bottom:20px}.ac-toolbar{display:flex;align-items:center;justify-content:space-between;gap:20px;padding:25px 26px}.ac-toolbar p{font-size:13px;margin-top:3px}.ac-search input{border:1px solid #e6dfe8;border-radius:10px;padding:10px 13px;background:#fbf9fc;min-width:230px;font-size:13px}.ac-member{display:flex;align-items:center;gap:14px;padding:19px 26px;border-top:1px solid #f4eef6}.ac-avatar{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:42px;height:42px;background:#f7f2fa;color:#391d44;border-radius:50%;font-size:13px;font-weight:600}.ac-member-name{display:flex;flex-direction:column;gap:2px;flex:1;min-width:150px}.ac-member-name strong{font-size:14px;font-weight:600}.ac-member-name strong small{background:#f6f2f8;color:#6b5a70;border-radius:5px;padding:2px 5px;font-size:10px;margin-left:8px;font-weight:400}.ac-member-name>span{font-size:13px;color:#6b5a70;overflow-wrap:anywhere}.ac-member-name>small{font-size:11px;color:#6b5a70}.ac-member-role{display:flex;gap:6px;align-items:flex-start;flex-direction:column;max-width:230px}.ac-badge{display:inline-block;background:#f6f2f8;border-radius:6px;color:#615167;padding:4px 8px;font-size:11px;line-height:1.35}.ac-row-actions{display:flex;align-items:center;gap:4px}.ac-member.inactive .ac-avatar{background:#f5eff7;color:#6b5a70}.ac-muted{font-size:11px}.ac-panel-note{border-top:1px solid #f4eef6;padding:17px 26px;color:#6b5a70;font-size:12px}.ac-menu{position:relative}.ac-menu summary{list-style:none;font-size:10px;letter-spacing:2px;cursor:pointer;padding:12px 8px;color:#6b5a70}.ac-menu summary::-webkit-details-marker{display:none}.ac-menu>div{position:absolute;right:0;top:100%;min-width:220px;z-index:3;padding:6px;border:1px solid #ede3f1;border-radius:12px;box-shadow:0 8px 32px #00000014;background:#fff}.ac-menu button{width:100%;text-align:left;background:transparent;border:0;border-radius:7px;padding:10px;font-size:12px}.ac-menu button:hover{background:#f7f3f9}.ac-section-intro{font-size:14px;margin-bottom:24px!important;max-width:700px}.ac-group-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:18px}.ac-group{padding:26px;min-width:0}.ac-group p{font-size:13px;margin:5px 0 14px}.ac-group-mark{font-size:27px;color:#391d44;margin-bottom:14px}.ac-group-actions{display:flex;gap:6px;flex-wrap:wrap;margin-top:26px}.ac-empty{text-align:center;padding:50px 24px}.ac-empty p{font-size:14px;max-width:420px;margin:10px auto 20px}.ac-empty-symbol{font-size:38px;color:#391d44;margin-bottom:16px}.ac-connect-intro{display:flex;align-items:flex-start;gap:22px;padding:28px;background:linear-gradient(120deg,#fff,#fbf9fd)}.ac-connect-intro p{font-size:14px;max-width:650px;margin:8px 0 15px}.ac-connect-symbol{width:48px;height:48px;display:grid;place-items:center;border-radius:13px;background:#f6f0f9;color:#391d44;font-size:26px;flex-shrink:0}.ac-key{display:flex;align-items:center;justify-content:space-between;gap:20px;padding:22px 26px;border-top:1px solid #f4eef6}.ac-key strong{font-size:14px}.ac-key p{display:flex;gap:10px;align-items:center;margin:7px 0}.ac-key code{font-size:12px;color:#6b5a70}.ac-key small{display:block;color:#6b5a70;font-size:11px;margin-top:5px}.ac-instructions{padding:26px}.ac-instructions>p{font-size:14px;margin:7px 0 20px}.ac-instructions label{font-size:12px;font-weight:600;display:block}.ac-copy-field{display:flex;gap:8px;align-items:stretch;margin-top:7px}.ac-copy-field input{min-width:0;flex:1;border:1px solid #e6dfe8;border-radius:9px;background:#fbf9fc;padding:10px 12px;font-family:ui-monospace,SFMono-Regular,monospace!important;font-size:12px!important}.ac-instructions details{margin-top:22px;border-top:1px solid #f2ebf5;padding-top:18px;font-size:13px}.ac-instructions summary{cursor:pointer;color:#391d44;font-weight:550}.ac-instructions details p{margin:12px 0}.ac-instructions pre{overflow:auto;background:#f9f5fa;border-radius:10px;padding:16px;color:#615167;font-size:12px}.ac-notice{display:flex;align-items:center;justify-content:space-between;gap:15px;padding:12px 16px;margin-bottom:18px;border-radius:11px;background:#eef7f2;color:#286c49;font-size:13px}.ac-notice.erreur{background:#fff1ef;color:#9d3027}.ac-loading{padding:40px;text-align:center;font-size:14px}.ac-dialog{border:1px solid #ece2f0;border-radius:20px;width:min(540px,calc(100vw - 32px));max-height:calc(100dvh - 48px);padding:0;color:#2a1632;box-shadow:0 24px 100px #0003;background:#fff}.ac-dialog::backdrop{background:#2a16324a;backdrop-filter:blur(5px)}.ac-dialog-head{display:flex;align-items:center;justify-content:space-between;padding:22px 26px;border-bottom:1px solid #f2ebf5;position:sticky;top:0;background:#fff;z-index:2}.ac-dialog-head h2{font-size:20px;letter-spacing:-.5px}.ac-form{padding:22px 26px 26px;display:flex;flex-direction:column;gap:18px}.ac-form>label{display:flex;flex-direction:column;gap:6px;font-size:13px;font-weight:550}.ac-form>label>input,.ac-form select{width:100%;min-height:42px;border:1px solid #e1dae4;border-radius:9px;padding:9px 11px;background:#fff;font-size:14px}.ac-form input:disabled{background:#f9f6fa;color:#6b5a70}.ac-form p{font-size:14px}.ac-form .ac-hint{font-size:12px;color:#6b5a70;line-height:1.6}.ac-role-choices,.ac-checks{margin:0;padding:0;border:0;display:flex;flex-direction:column;gap:8px}.ac-form legend{font-size:13px;font-weight:600;margin-bottom:10px}.ac-form legend span{font-size:11px;font-weight:400;color:#6b5a70;margin-left:5px}.ac-role-choices label{display:flex;align-items:flex-start;gap:10px;border:1px solid #eee4f1;padding:12px;border-radius:10px;cursor:pointer}.ac-role-choices label.selectionne{border-color:#391d44;background:#faf8fc}.ac-role-choices input,.ac-checks input{margin:3px 0 0;accent-color:#391d44;flex-shrink:0}.ac-role-choices strong,.ac-checks strong{font-size:13px;font-weight:550}.ac-role-choices small,.ac-checks small{display:block;color:#6b5a70;font-size:11px;margin-top:2px;line-height:1.5}.ac-checks label{display:flex;align-items:flex-start;gap:10px;padding:7px 0;font-size:13px;cursor:pointer}.ac-form label.ac-inline-check{display:flex;flex-direction:row;align-items:center;gap:8px;font-size:12px;font-weight:400}.ac-inline-check input{width:auto;min-height:0}.ac-dialog-actions{display:flex;justify-content:flex-end;gap:9px;margin-top:3px}.ac-form-error:empty{display:none}.ac-form-error{color:#b52b26!important;font-size:12px!important}.acces-pane :focus-visible{outline:3px solid #391d4466;outline-offset:3px}.ac-sr{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0!important}@media(max-width:720px){.ac-page-head{align-items:flex-start;flex-direction:column;gap:20px}.ac-toolbar{padding:20px;align-items:flex-start;flex-direction:column;gap:15px}.ac-search,.ac-search input{width:100%;min-width:0}.ac-member{display:grid;grid-template-columns:38px minmax(0,1fr) auto;padding:18px;gap:10px}.ac-avatar{width:36px;height:36px}.ac-member-name{min-width:0}.ac-member-role{grid-column:2;grid-row:2;max-width:none}.ac-row-actions{grid-column:3;grid-row:1/3;flex-direction:column;align-items:flex-end}.ac-member-name strong{font-size:13px}.ac-member-name>span{font-size:11px}.ac-key{padding:20px;align-items:flex-start}.ac-key p{align-items:flex-start;flex-direction:column;gap:4px}.ac-connect-intro{padding:22px;gap:15px}.ac-connect-symbol{width:36px;height:36px;font-size:22px}.ac-instructions{padding:20px}.ac-dialog-head{padding:18px 20px}.ac-form{padding:20px}.ac-tabs{max-width:100%}.ac-tabs button{padding:8px 12px}.ac-copy-field{flex-wrap:wrap}.ac-copy-field input{flex-basis:70%}.ac-dialog-actions .ac-button{flex:1}}
.ac-instructions>div[aria-label]{margin:14px 0}.ac-instructions>section{border-top:1px solid #f2ebf5;margin-top:24px;padding-top:22px}.ac-instructions h3{margin:0 0 10px}.ac-instructions h4{font-size:14px;margin:22px 0 8px}.ac-instructions li{color:#615167;font-size:13px;line-height:1.6;margin:5px 0}.ac-instructions a{color:#391d44}.ac-instructions pre{white-space:pre-wrap;overflow-wrap:anywhere}.ac-instructions [role=tabpanel]{background:#fbf9fc;border:1px solid #f2ebf5;border-radius:12px;padding:20px;margin-top:0}.ac-instructions [role=tabpanel][hidden]{display:none}.ac-instructions [role=tabpanel] p{font-size:13px}.ac-instructions [role=tabpanel] pre+button{margin-bottom:8px}
`
